import { useQuery } from '@apollo/client/react';
import { useCookies } from '@fergdigitalcommerce/fergy-react-components';
import loadable from '@loadable/component';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type ReactElement, useContext } from 'react';
import type { ProductPageQuery, ProductPageQueryVariables } from '../../../__generated__/graphql-client-types';
import { PageLoading } from '../../../components/common-components/loading/loading.component';
import { FiveHundred } from '../../../components/error-components/500/500.component';
import { ProductNotFound } from '../../../components/product-components/product-not-found/product-not-found.component';
import { ProductPageSkeleton } from '../../../components/product-page-skeleton/product-page-skeleton.component';
import { COOKIE_LAST_PRODUCT } from '../../../constants/cookies';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';
import { DEFAULT_PDP_PRODUCT_STATUS } from '../../../constants/product';
import { AddToCartContextProvider } from '../../../contexts/add-to-cart/add-to-cart.context';
import { ProductFamilyContextProvider } from '../../../contexts/product-family/product-family.context';
import { ProductStickyNavContextProvider } from '../../../contexts/product-sticky-nav/product-sticky-nav.context';
import { VariantContext, VariantContextProvider } from '../../../contexts/variant/variant.context';
import { populateProductData } from '../../../helpers/datalayer-helper/internal-datalayer.helper';
import { getDatalayerForProductPage } from '../../../helpers/datalayer-helper/pdp-datalayer.helper';
import { booleanFromQueryString } from '../../../helpers/general-helper/general-helper';
import { findVariant, isProductNotFound, hasNonStockFinishes } from '../../../helpers/product-helper/product.helper';
import { useTrackPageView } from '../../../hooks/analytics/analytics.hooks';
import { useSiteViewPreference } from '../../../hooks/apollo/employee/employee.hooks';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useUpdateUrlOnVariantChange } from '../../../hooks/product/product.hooks';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { PRODUCT_PAGE } from '../../../queries/product/product.queries';
import type { PdpProductFamily } from '../../../types/product.types';
import { ProductDetailPage } from '../product-details/product-details.page';

// dynamically import discontinued product page
const LoadableDiscontinuedProduct = loadable(
	() => import(/* webpackChunkName: "discontinuedproduct" */ '../discontinued-product/discontinued-product.page'),
	{
		resolveComponent: ({ DiscontinuedProduct }) => DiscontinuedProduct
	}
);

type AnalyticsProviderProps = {
	children: ReactElement;
	product: PdpProductFamily;
	isLoading: boolean;
	isDiscontinued: boolean;
};

/**
 * Wrapper component that has the sole responsibility of firing page view analytics
 */
const AnalyticsProvider: FunctionComponent<AnalyticsProviderProps> = ({ children, product, isLoading, isDiscontinued }) => {
	const { isSelectedVariantConfirmed, selectedVariant } = useContext(VariantContext);
	const pageTemplate = product?.configurationType;
	const productDataLayer = getDatalayerForProductPage(product, selectedVariant, { pageTemplate });

	const pageName = 'product:display';

	useTrackPageView(
		{
			pageName: isDiscontinued ? `discontinued-${pageName}` : pageName,
			businessCategory: product?.businessCategory?.name,
			baseCategory: product?.baseCategory?.name,
			brand: product?.brandInfo?.name,
			collection: product?.collection?.name || ''
		},
		productDataLayer,
		isLoading || selectedVariant === undefined || (!isSelectedVariantConfirmed && !isDiscontinued)
	);

	return children;
};

export type ProductRouterProps = { familyId: number };

export const ProductRouter: FunctionComponent<ProductRouterProps> = ({ familyId }) => {
	const skeletonLoaderActive = useFeature(FEATURE_FLAGS.PRODUCT_PAGE_SKELETON_LOADER, { isSiteWide: true });
	const updateUrl = useUpdateUrlOnVariantChange();
	const { pathname, search } = useRouterLocation();
	const { showAsEmployee, employee } = useSiteViewPreference();
	/**
	 * We set this last product cookie to be compliant with other pages that
	 * use this data (including pages on node-store)
	 */
	const { setCookie } = useCookies();
	setCookie(COOKIE_LAST_PRODUCT, `${pathname}${search}`);

	const searchParams = new URLSearchParams(search);
	// '?ns=true' changes how we route requests for nonstock products
	const nonstockFlag = booleanFromQueryString(searchParams.get('ns') || '');
	const uid = Number(searchParams.get('uid') || '0');
	const regionId = searchParams.get('regionid') || undefined;

	const { data, loading, error, refetch } = useQuery<ProductPageQuery, ProductPageQueryVariables>(PRODUCT_PAGE, {
		variables: { id: familyId, productStatus: DEFAULT_PDP_PRODUCT_STATUS, regionId },
		skip: !familyId,
		fetchPolicy: 'network-only' // using default policy here can cause issues with add-to-cart functionality
	});

	const product = data?.productFamily;

	if (loading) {
		return skeletonLoaderActive ? <ProductPageSkeleton /> : <PageLoading />;
	}

	// IMPROVEMENT: Generic errors should be handled by the PageContainer.
	if (error) {
		return <FiveHundred />;
	}

	if (!product || isProductNotFound(product)) {
		return <ProductNotFound />;
	}

	const initialVariant = findVariant(product, uid, ['stock', 'nonstock', 'pending', 'discontinued']);

	// this should not happen but it might be possible for findVariant to fail to come up with an initial variant
	if (!initialVariant) {
		logError(new Error(`could not find variant for: ${product.id} with uniqueId ${uid}`));
		return <ProductNotFound />;
	}

	if (initialVariant.status === 'pending' && (!showAsEmployee || !nonstockFlag)) {
		return <ProductNotFound />;
	}

	const productUid = Number(uid) || initialVariant?.id || 0;

	// populate datalayer for internal tools
	if (employee.isAuthenticated) {
		populateProductData(product, initialVariant);
	}

	/**
	 * Discontinued template is used for all discontinued products, EXCEPT if
	 * they are nonstock and the 'ns' query param was provided and true.
	 * Pending products should not be shown as discontinued regardless of the isDiscontinued flag on the product.
	 */
	const isProductViewable = nonstockFlag && hasNonStockFinishes(product);

	const routeToDiscontinued = initialVariant.status !== 'pending' && product.isDiscontinued && !isProductViewable;

	return (
		<ProductFamilyContextProvider {...product}>
			<VariantContextProvider onSelection={updateUrl} {...initialVariant}>
				<AddToCartContextProvider>
					<ProductStickyNavContextProvider>
						<AnalyticsProvider product={product} isLoading={loading} isDiscontinued={routeToDiscontinued}>
							{routeToDiscontinued ? (
								<LoadableDiscontinuedProduct productUid={productUid} productFamilyId={familyId} pageName="pdp" />
							) : (
								<ProductDetailPage
									product={product}
									productUid={productUid}
									refetchData={refetch}
									nonStockMode={nonstockFlag}
								/>
							)}
						</AnalyticsProvider>
					</ProductStickyNavContextProvider>
				</AddToCartContextProvider>
			</VariantContextProvider>
		</ProductFamilyContextProvider>
	);
};
